/**
 * Authentication: an email, a password, and a cookie the browser keeps.
 *
 * There is no SDK and no build-time switch. The session lives in an HttpOnly
 * cookie this code cannot read, which is the point — the only thing the app
 * knows about its own session is what `GET /api/me` answers, and that is the
 * server's opinion rather than a decoded token's.
 *
 * `mode` is discovered rather than configured. A controller running with
 * `dev_no_auth` answers `/api/me` without a cookie, and that *is* dev mode; a
 * bundle can no longer be built that is structurally unable to sign in.
 *
 * Authentication is only half the story. Being signed in proves who you are;
 * whether you may read every clinic's configuration comes from
 * `platform_admins` and arrives as a 403. That second question is answered in
 * `AuthGate`, not here, because the distinction is what stops a non-operator
 * from being shown an empty dashboard.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { apiPost } from './api';

export type AuthMode = 'local' | 'none';

export interface AuthState {
  mode: AuthMode;
  /** True while a sign-in request is in flight. */
  isLoading: boolean;
  /** What this app believes; `/api/me` is authoritative. */
  isAuthenticated: boolean;
  accountEmail: string | null;
  error: Error | null;
  /** Resolves on success, throws with a message to show on failure. */
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

interface LoginResult {
  email: string;
  role: string;
}

const AuthContext = createContext<AuthState>({
  mode: 'local',
  isLoading: false,
  isAuthenticated: false,
  accountEmail: null,
  error: null,
  login: async () => undefined,
  logout: () => undefined,
});

export function useAuth(): AuthState {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isLoading, setLoading] = useState(false);
  const [accountEmail, setAccountEmail] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const login = useCallback(async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiPost<LoginResult>('/api/auth/login', {
        email,
        password,
      });
      setAccountEmail(result.email);
      // A full reload rather than a state flip. Every page's data was fetched
      // as nobody, so the cheapest correct thing is to start again with the
      // cookie in place — and it also drops any stale error state the login
      // screen was holding.
      window.location.reload();
    } catch (cause) {
      const failure = cause instanceof Error ? cause : new Error(String(cause));
      setError(failure);
      throw failure;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    // Fire the request, then reload regardless of its outcome: the server
    // revokes whatever was presented, and the browser must stop presenting it
    // either way. Waiting for a response that may never come would leave an
    // operator looking at a signed-in page they believe they have left.
    void apiPost('/api/auth/logout')
      .catch(() => undefined)
      .finally(() => {
        window.location.assign('/');
      });
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      mode: 'local',
      isLoading,
      isAuthenticated: accountEmail !== null,
      accountEmail,
      error,
      login,
      logout,
    }),
    [isLoading, accountEmail, error, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
